import { Box, Button, Checkbox, Menu, MenuItem, ListItemText, Typography, useTheme } from "@mui/material";
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { bgBlur } from 'src/theme/css';
import { useResponsive } from 'src/hooks/use-responsive';
import Logo from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';
import { useDateRangeSelect } from 'src/components/generic-select/date-range-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import Iconify from 'src/components/iconify';
import DateRangeSelect from 'src/components/generic-select/date-range-select';
import MonthRangeSelect from 'src/components/generic-select/month-range-select';
import { NON_BRAND_SEARCH_PERF_PATH, monthRangeActions, toMonthRangeValue } from 'src/utils/month-range-filters';
import { fDate, fLocalDay, getDateRangeIso } from 'src/utils/format-time';
import { useSearchTermsFiltersQuery } from 'src/context/api/non-branded/api';
import { ROUTES_WITH_TAGS, ROUTES_WITHOUT_FILTER_BAR, isNonBrandSearchPerf, platformsPillOptions, showClustersPill, showPlatformsPill, usesRedesignedHeader } from './header-routes';
import { useDispatch, useSelector } from "react-redux";
import InfiniteScrollKeywords from "src/sections/overview/dashboards/views/InfiniteScrollKeywords";
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { FlagIcon } from "src/components/iconify/flagIcon";
import { subDays, differenceInDays } from "date-fns";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useLocales } from "src/locales";
import { useCurrentUserQuery } from "src/context/api/auth";
import { RootState } from "src/context/store";
import { useSearchParams } from "react-router-dom";
import { useRouter, usePathname } from "src/routes/hooks";
import { NavbarProps, AdType } from "src/components/visibility/types";
import { setAdType as setAdTypeAction, setSortBy as setSortByAction, setSortDirection as setSortDirectionAction } from 'src/context/reducers/visibility/adsSlice';
import { setFilterDate, setFilterDevice, setFilterCountry, setFilterKeyword, setFilterTimestep, setFilterDateCompare, setFilterPlatform, setFilterMinDatePicker, setExtraOptions, setSavingsFormulaFilter, setFilterLoadedDevice, setFilterLoadedPlatform, setFilterLoadedCountry, setFilterLoadedKeyword, setFilterTags, setFilterLoadedTags, setFilterKeywordPLA } from '../../context/reducers/filter-settings/filterSlice';
import { getSavingsFormulaOptions } from './savings-formula-utils';
import { AccountPopover, NotificationsPopover } from '../_common';
import { HEADER, NAV } from '../config-layout';
import countriesJson from "../../utils/countries.json"
import SettingsPanel from "../_common/settings/settings-panel";
import { useLocalFeatureEnabled } from "src/hooks/use-local-feature";
import { useClusterManagerVisible } from "src/hooks/use-cluster-manager-visible";
import { getTabs } from "./header-tabs";
import { useCountriesQuery, usePlatformsQuery, useSearchTermsQuery } from "src/context/api/filters/api";
import GenericInfiniteScrollList from "src/sections/overview/dashboards/views/generic-infinite-scroll-list";
import { useGetSearchTermsByTagsQuery, useGetTagsListQuery } from "src/context/api";
import { generateColorFromString } from "src/sections/cluster-manager/cluster-color";
import { useResetModuleFilters } from "src/sections/cluster-manager/use-reset-module-filters";
import { HeaderTabs } from "./headerTabs";
import AdsFiltersDrawer from "src/components/visibility/components/AdsFiltersDrawer";
import OpportunityFilterPill from "src/sections/overview/dashboards/views/non-branded/opportunity-filter-pill";
import { useNonBrandPlatforms } from "src/sections/overview/dashboards/views/non-branded/use-non-brand-platforms";
import { resolveTimestep } from "src/utils/visibility-timestep";
import { pickedClusters } from 'src/utils/cluster-filter';

type Props = {
  onOpenNav?: VoidFunction;
  navExpanded?: boolean;
  navPinned?: boolean;
};

// Competitive Intelligence Ads Header - moved out of Header to comply with lint rule
type CIAHeaderProps = Partial<NavbarProps>;

const VisibilityAdsHeader = ({
  adType,
  counts = { ALL: 0, PLA: 0, TEXT: 0, RightPanel: 0 },
  searchQuery,
  sortBy,
  sortDirection,
}: CIAHeaderProps) => {
  const dispatch = useDispatch();
  const ui = useSelector((state: any) => state.visibilityAdsUi || { adType: 'ALL', searchQuery: '', sortBy: 'Freq.', sortDirection: 'desc' });
  const effectiveAdType = adType ?? ui.adType;
  const effectiveSortBy = sortBy ?? ui.sortBy;
  const effectiveSortDirection = sortDirection ?? ui.sortDirection;

  const hasPla = useSelector(
    (state: any) => state.visibilityAdsUi.hasPla
  );

  const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);
  const typeOpen = Boolean(typeAnchorEl);
  const closeTypeMenu = () => setTypeAnchorEl(null);

  // Dropdown for sorting
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortOpen = Boolean(sortAnchorEl);
  const closeSortMenu = () => setSortAnchorEl(null);
  const { data: user } = useCurrentUserQuery();

  const role = user?.role;

  const filterStore = useSelector((state: any) => state.filters);
  const displayTags = useMemo(() => {
    return ["/brand/visibility/ads"].includes(location.pathname)
  }, [location.pathname, role])


  const items = useMemo(() => ([
    { key: 'ALL' as AdType, label: 'All Ads' },
    { key: 'PLA' as AdType, label: 'PLA' },
    { key: 'TEXT' as AdType, label: 'Text Ads' },
  ]), [counts.ALL, counts.PLA, counts.TEXT]);

  const handleSortChange = (sort: 'Freq.' | 'Position' | 'Price') => {
    if (sort === effectiveSortBy) {
      dispatch(
        setSortDirectionAction(
          effectiveSortDirection === 'asc' ? 'desc' : 'asc'
        )
      );
    } else {
      dispatch(setSortByAction(sort));
      dispatch(
        setSortDirectionAction(sort === 'Position' ? 'asc' : 'desc')
      );
    }
  };

  const handleKeywordFilterChange = (value: any) => {
    if (filterStore?.keywords) {
      if (filterStore?.keywords.includes(value)) {
        dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
      } else {
        const updatedKeywords = [...filterStore.keywords, value];
        dispatch(setFilterKeyword(updatedKeywords))
      }
    }
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
      {/* Left side: Ads type + filter icon */}
      <Stack direction="row" alignItems="center" spacing={1}>

        {displayTags
          && filterStore.loadedTags.length > 0 && (
            <Box>
              <GenericInfiniteScrollList
                disableClearable={filterStore.tags.length === 0}
                limitTags={2}
                sx={{
                  width: 170,
                  '& .MuiAutocomplete-root': {
                    height: 36,
                  },

                  '& .MuiAutocomplete-inputRoot': {
                    minHeight: '36px !important',
                    height: '36px',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    overflow: 'hidden',
                    py: '0 !important',
                  },

                  '& .MuiAutocomplete-tag': {
                    height: 24,
                    maxWidth: 120,
                    my: '0 !important',
                  },

                  '& .MuiAutocomplete-input': {
                    minWidth: '0 !important',
                    width: '0 !important',
                    p: '0 !important',
                  },

                  '& .MuiOutlinedInput-root': {
                    minHeight: '36px !important',
                    height: '36px',
                    py: '0 !important',
                  },
                }}
                fullWidth={true}
                searchPlaceholder=""
                items={(filterStore as RootState["filters"]).loadedTags}
                options={(filterStore as RootState["filters"]).loadedTags}
                multiple={true}
                value={
                  filterStore.tags.length === 0
                    ? [{ id: "__default__", label: "All Clusters" }]
                    : filterStore.tags
                }
                onSelectionChange={(selected) => {
                  dispatch(setFilterTags(pickedClusters(selected, filterStore.loadedTags)));
                }}
                getOptionLabel={(item: any) => item?.label || (item as any)}
                getOptionKey={(item: any) => item.id || (item as any)}
                isOptionEqualToValue={(item, value) => (item.id || item) === (value.id || value)}
                customItemRenderer={(item) => (
                  <Chip
                    label={item.label || (item as any)}
                    size="small"
                    variant="soft"
                    sx={{
                      backgroundColor: item?.label
                        ? generateColorFromString(String(item.label))
                        : undefined,
                      color: "#fff"
                    }}
                  />
                )}
                customTagRenderer={(item) => {
                  const isDefault = item.id === "__default__";

                  return (
                    <Chip
                      label={item.label}
                      size="small"
                      variant={isDefault ? "soft" : "soft"} // style différent
                      sx={{
                        fontSize: '13px',
                        backgroundColor: 'rgba(145, 158, 171, 0.12)',
                        color: 'rgba(51, 60, 71, 1)',
                      }}
                      onDelete={
                        isDefault
                          ? undefined
                          : () => {
                            dispatch(
                              setFilterTags(
                                filterStore.tags.filter(
                                  (tag: any) => tag.id !== item.id
                                ) as any
                              )
                            );
                          }
                      }
                    />
                  );
                }}
              />
            </Box>
          )}

        <InfiniteScrollKeywords
          keywords={(filterStore?.loadedKeyword || [])}
          showItemCount={false}
          selectedKeywords={filterStore?.keywordsPLA}
          setSelectedKeywords={(keywords) =>
            dispatch(setFilterKeywordPLA(keywords))
          }
          placeholderAll={"All ST"}
          width={200}
          onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
        />

        <AdsFiltersDrawer hasPla={hasPla} />
      </Stack>

      {/* Right side: Search + Sort by */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: '#919EAB',
              fontFamily: '"Public Sans", sans-serif',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '23px',
              whiteSpace: 'nowrap',
            }}
          >
            Sort By
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: '4px',
              border: '1px solid rgba(145, 158, 171, 0.20)',
              borderRadius: '8px',
              background: '#FFF',
              gap: '4px',
            }}
          >
            <Button
              onClick={() => handleSortChange('Freq.')}
              startIcon={effectiveSortBy === 'Freq.' && <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                <path d="M14 10.6654L11.3333 13.332L8.66667 10.6654M11.3333 13.332L11.3333 2.66536M8.66667 13.332L2 13.332M6.66667 10.6654L2 10.6654M4.66667 7.9987L2 7.9987" stroke="#333C47" stroke-linecap="round" stroke-linejoin="round" />
              </svg>} sx={{
                height: 32,
                px: '12px',
                minWidth: 0,
                borderRadius: '6px',
                background:
                  effectiveSortBy === 'Freq.'
                    ? 'rgba(145, 158, 171, 0.20)'
                    : 'transparent',

                color: '#333C47',
                fontFamily: '"Public Sans", sans-serif',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '23px',
                textTransform: 'none',

                '& .MuiButton-startIcon': {
                  mr: 1,
                  color: '#333C47',
                },

                '&:hover': {
                  background: 'rgba(145, 158, 171, 0.20)',
                },
              }}
            >
              Frequency
            </Button>

            <Button
              onClick={() => handleSortChange('Position')}
              startIcon={effectiveSortBy === 'Position' && <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                <path d="M14 10.6654L11.3333 13.332L8.66667 10.6654M11.3333 13.332L11.3333 2.66536M8.66667 13.332L2 13.332M6.66667 10.6654L2 10.6654M4.66667 7.9987L2 7.9987" stroke="#333C47" stroke-linecap="round" stroke-linejoin="round" />
              </svg>}

              sx={{
                height: 32,
                px: '12px',
                minWidth: 0,
                borderRadius: '6px',
                background:
                  effectiveSortBy === 'Position'
                    ? 'rgba(145, 158, 171, 0.20)'
                    : 'transparent',

                color: '#333C47',
                fontFamily: '"Public Sans", sans-serif',
                fontSize: '13px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '23px',
                textTransform: 'none',

                '&:hover': {
                  background: 'rgba(145, 158, 171, 0.20)',
                },
              }}
            >
              Position
            </Button>

            {hasPla &&
              <Button
                onClick={() => handleSortChange('Price')}
                startIcon={effectiveSortBy === 'Price' && <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                  <path d="M14 10.6654L11.3333 13.332L8.66667 10.6654M11.3333 13.332L11.3333 2.66536M8.66667 13.332L2 13.332M6.66667 10.6654L2 10.6654M4.66667 7.9987L2 7.9987" stroke="#333C47" stroke-linecap="round" stroke-linejoin="round" />
                </svg>}
                sx={{
                  height: 32,
                  px: '12px',
                  minWidth: 0,
                  borderRadius: '6px',
                  background:
                    effectiveSortBy === 'Price'
                      ? 'rgba(145, 158, 171, 0.20)'
                      : 'transparent',

                  color: '#333C47',
                  fontFamily: '"Public Sans", sans-serif',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '23px',
                  textTransform: 'none',

                  '&:hover': {
                    background: 'rgba(145, 158, 171, 0.20)',
                  },
                }}
              >
                Price
              </Button>
            }
          </Box>
        </Box>
      </Stack>

      {/* Type menu */}
      <Menu
        anchorEl={typeAnchorEl}
        open={typeOpen}
        onClose={closeTypeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { textAlign: 'right', minWidth: 90, maxWidth: 90 } }}
        slotProps={{ paper: { sx: { mt: 1 } } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            selected={item.key === effectiveAdType}
            onClick={() => {
              dispatch(setAdTypeAction(item.key));
              closeTypeMenu();
            }}
          >
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={sortOpen}
        onClose={closeSortMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 2, minWidth: 135 } } }}
      >
        {['Freq.', 'Position', 'Price'].map((opt) => (
          <MenuItem
            key={opt}
            selected={opt === effectiveSortBy}
            onClick={() => {
              const newSort = opt as any;
              if (newSort === effectiveSortBy) {
                dispatch(setSortDirectionAction(effectiveSortDirection === 'asc' ? 'desc' : 'asc'));
              } else {
                dispatch(setSortByAction(newSort));
                dispatch(setSortDirectionAction(newSort === 'Position' ? 'asc' : 'desc'));
              }
              closeSortMenu();
            }}
          >
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

export default function Header({ onOpenNav, navExpanded = false, navPinned = false }: Props) {
  const theme = useTheme();
  const { t } = useLocales();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [hasUserAdjustedRange, setHasUserAdjustedRange] = useState(false);

  const filterStore = useSelector((state: any) => state.filters);

  const workspace: any = useCurrentWorkspace();
  const optionRefs: any = useRef({});

  const [compareStartDate, setCompareStartDate] = useState<any>(filterStore?.compareStart);
  const [compareEndDate, setCompareEndDate] = useState<any>(filterStore?.compareEnd);
  const [searchParams,] = useSearchParams();
  const settings = useSettingsContext();

  const extraOptions = useSelector((state: RootState) => state.filters.extraOptions);
  const router = useRouter()
  const pathname = usePathname();

  const { data: user } = useCurrentUserQuery();
  const role = user?.role;

  const localEnabled = useLocalFeatureEnabled();
  const clusterManagerVisible = useClusterManagerVisible();

  const tabs = getTabs(role, { localEnabled, clusterManagerVisible });


  const dispatch = useDispatch()
  const displayTags = useMemo(() => {
    return ROUTES_WITH_TAGS.includes(location.pathname)
  }, [location.pathname, role])

  useEffect(() => {
    if (searchParams.has('mode') && extraOptions.mode !== searchParams.get('mode')) {
      dispatch(setExtraOptions({ ...extraOptions, mode: searchParams.get('mode') as "base" | "activations" }));
    }
  }, [searchParams, dispatch, extraOptions]);

  useEffect(() => {
    if (isFilterDrawerOpen) setIsFilterDrawerOpen(false);
  }, [pathname]);

  const isNavHorizontal = settings.themeLayout === 'horizontal';
  const isNavMini = settings.themeLayout === 'mini';
  const lgUp = useResponsive('up', 'lg');
  const offsetTop = false;

  const [start, end] = useMemo(() => {
    const startDate = new Date();
    startDate.setMonth(startDate.getUTCMonth() - 1);
    startDate.setDate(startDate.getUTCDate());
    const endDate = new Date();
    endDate.setDate(endDate.getUTCDate() - 1);
    return [startDate, endDate];
  }, []);

  const dateRangeSelect = useDateRangeSelect({
    preset: "last_30d",
    step: "year_week",
    selected: "custom",
    start,
    end
  });

  useEffect(() => {
    dispatch(setFilterDateCompare({
      compareStart: fLocalDay(compareStartDate),
      compareEnd: fLocalDay(compareEndDate),
      skipAlignment: true
    }))
  }, [compareStartDate, compareEndDate, dispatch])

  const updateFilterDate = (startDate: Date | null, endDate: Date | null, skipAlignment = true) => {
    if (startDate && endDate) {
      setHasUserAdjustedRange(true);
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { start, end } = getDateRangeIso(startDate, endDate, userTimeZone);

      dispatch(setFilterDate({ start, end, skipAlignment }));

      // Calculer et mettre à jour la granularité
      const daysDifference = differenceInDays(endDate, startDate);
      let newGranularity = 'date_day';
      if (daysDifference > 365) {
        newGranularity = 'year_month';
      } else if (daysDifference > 90) {
        newGranularity = 'year_week';
      }
      dispatch(setFilterTimestep({ timestep: resolveTimestep(newGranularity, location?.pathname), skipAlignment }));
    }
  };

  // Routes that manage their own default date range - Header should not overwrite
  const routesWithOwnDateDefaults = [
    '/non-brand/search-performance',
    '/non-brand/search-performance/cluster-manager',
    '/non-brand/visibility',
    '/brand/search-performance/overview',
    '/brand/search-performance/traffic',
    '/brand/search-performance/explore',
    '/brand/search-performance/conversions',
    '/brand/search-performance/cluster-manager'
  ];

  useEffect(() => {
    // Toujours utiliser le fuseau local de l'utilisateur
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (filterStore?.datesForcedByActivation) return;
    if (routesWithOwnDateDefaults.includes(location?.pathname)) return;
    if (location?.pathname?.startsWith('/brand/visibility')) {
      return; // VisibilityLayout owns date defaults; preserve custom range across sub-pages
    }

    if (lgUp && !workspace?.name.includes('Demo')) {
      if (dateRangeSelect?.start && dateRangeSelect?.end) {
        // Range utilisateur standard
        const { start, end } = getDateRangeIso(dateRangeSelect.start, dateRangeSelect.end, userTimeZone);
        if (filterStore?.start !== filterStore?.end) {
          const skipAlignment = hasUserAdjustedRange;
          dispatch(setFilterDate({ start, end, skipAlignment }));
        }
      }
    } else {
      if (lgUp) {
        if (dateRangeSelect?.start && dateRangeSelect?.end && filterStore.timestep === "date_day") {
          const newStartDate = new Date(2024, 8, 1); // 1er sept 2024
          const newEndDate = new Date(2024, 10, 30); // 30 nov 2024

          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        } else {
          const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
          const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024
          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        }
      }
    }
  }, [dateRangeSelect]);

  const handleDeviceFilterChange = (selectedOptions: any) => {
    let updatedOptions;

    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions
        .filter((option: string) => option !== t('dashboard.filters.all_devices'))
        .map((option: string) => option.toUpperCase());
    } else {
      updatedOptions =
        selectedOptions !== t('dashboard.filters.all_devices')
          ? [selectedOptions.toUpperCase()]
          : [];
    }

    dispatch(setFilterDevice(updatedOptions));
  };


  const handleCountryFilterChange = (selectedOptions: any) => {
    let updatedOptions;
    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions.filter(
        (option: string) => option !== t('dashboard.filters.all_countries')
      );
    } else {
      updatedOptions = selectedOptions !== t('dashboard.filters.all_countries')
        ? [selectedOptions]
        : [];
    }

    if (location?.pathname === "/brand-wayback") {
      dispatch(setFilterKeyword([]))
    }

    dispatch(setFilterCountry(updatedOptions));


  };

  const handleKeywordFilterChange = (value: any) => {
    if (filterStore?.keywords) {
      if (filterStore?.keywords.includes(value)) {
        dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
      } else {
        const updatedKeywords = [...filterStore.keywords, value];
        dispatch(setFilterKeyword(updatedKeywords))
      }
    }
  };

  useEffect(() => {
    if (routesWithOwnDateDefaults.includes(location?.pathname)) {
      return; // Let the page component set its own dates (e.g. last 12 weeks)
    }
    if (location?.pathname?.startsWith('/brand/visibility')) {
      return; // VisibilityLayout owns date defaults; preserve custom range across sub-pages
    }

    const newStartDate = new Date();
    newStartDate.setMonth(newStartDate.getUTCMonth() - 1);
    newStartDate.setDate(newStartDate.getUTCDate());

    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getUTCDate() - 1);

    dateRangeSelect.onChangeStartDate(newStartDate);
    dateRangeSelect.onChangeEndDate(newEndDate);

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

    dispatch(setFilterDate({ start, end }));

    dispatch(setFilterDateCompare({
      compareStart: subDays(new Date(), 60).toISOString(),
      compareEnd: subDays(new Date(), 31).toISOString(),
    }))
  }, [location?.pathname])

  const onChangePreset = (value: string) => {
    dateRangeSelect.onChangePreset(value);
    setHasUserAdjustedRange(false);
  };

  const onChangeTimeStep = (value: string) => {
    dispatch(setFilterTimestep(value));
  }

  const iconDevices: any = [
    { name: "Tablet", icon: "icon-park:ipad" },
    { name: "Mobile", icon: "icon-park:iphone" },
    { name: "Desktop", icon: "icon-park:i-mac" }
  ]

  const handleOnlyFilter = (option: string) => {
    dispatch(setFilterCountry([option]));
  };

  const savingsFormulaOptions = getSavingsFormulaOptions(workspace?.savings, workspace?.savings_custom_available);

  const savingsFormulaFilter = savingsFormulaOptions.find(
    (opt) => opt.value === filterStore.savingsFormulaFilter
  ) ?? savingsFormulaOptions[0];

  useEffect(() => {
    if (workspace?.name.includes('Demo')) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
      const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024

      const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

      dispatch(setFilterDate({ start, end }));
      dispatch(setFilterTimestep(resolveTimestep("date_day", location?.pathname)))
    }
  }, [workspace, location?.pathname])


  const { data: platforms, isLoading: isLoadingPlatforms } = usePlatformsQuery(
    {
      workspace: workspace?.bqTable,
    },
    { skip: !workspace?.bqTable }
  );

  const shouldSkip = !workspace?.bqTable;

  const { data: countries, isLoading: isLoadingCountries } = useCountriesQuery(
    {
      workspace: workspace?.bqTable,
      platforms: filterStore?.platforms,
      startDate: filterStore?.start,
      endDate: filterStore?.end
    },
    { skip: shouldSkip }
  );

  // Non Brand Search Performance offers Non Brand search terms, not the Brand list.
  const onNonBrandSearchPerf = isNonBrandSearchPerf(location.pathname);
  const nonBrandPlatforms = useNonBrandPlatforms(onNonBrandSearchPerf);
  const pillPlatforms = platformsPillOptions(location.pathname, filterStore?.loadedPlatform ?? [], nonBrandPlatforms);

  const { data: searchTerms } = useSearchTermsQuery(
    {
      workspace: workspace?.bqTable,
      platforms: filterStore?.platforms,
      devices: filterStore?.devices,
      startDate: filterStore?.start,
      countries: filterStore?.countries,
      endDate: filterStore?.end
    },
    // The Brand list is never shown on Non Brand Search Performance: do not fetch it there.
    { skip: shouldSkip || onNonBrandSearchPerf }
  );

  const { data: nonBrandSearchTerms } = useSearchTermsFiltersQuery(
    {
      workspace_id: workspace?.id as number,
      start_date: fDate(filterStore?.start, 'yyyy-MM-dd'),
      end_date: fDate(filterStore?.end, 'yyyy-MM-dd'),
      devices: filterStore?.devices,
      countries: filterStore?.countries,
      platforms: filterStore?.platforms,
    },
    { skip: !onNonBrandSearchPerf || !workspace?.id }
  );
  const keywordSource: { search_terms?: any[] } | undefined = onNonBrandSearchPerf
    ? (nonBrandSearchTerms ? { search_terms: nonBrandSearchTerms } : undefined)
    : searchTerms;

  const clusterModule = useResetModuleFilters(location.pathname);

  const { data: searchTermsByTags } = useGetSearchTermsByTagsQuery(
    {
      workspaceId: workspace?.id as number,
      tags: filterStore?.tags.map((tag: any) => tag.label),
      module: clusterModule,
    },
    { skip: !workspace?.id || filterStore?.tags.length === 0 }
  );

  const { data: { tags } = {} } = useGetTagsListQuery(
    {
      workspaceId: workspace?.id as number,
      module: clusterModule,
    },
    { skip: !workspace?.id }
  );

  // FILTER MIN DATE
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const minDate = `${currentYear - 2}-01-01`;

    dispatch(setFilterMinDatePicker(minDate));
  }, [location?.pathname, workspace])

  // FILTER COUNTRIES
  useEffect(() => {
    if (countries?.countries?.length > 0) {
      dispatch(setFilterLoadedCountry(countries.countries));
    }
  }, [countries?.countries, workspace, location?.pathname]);

  // FILTER PLATFORMS
  useEffect(() => {
    // An empty list is dispatched too: skipping it kept the previous workspace's platforms.
    if (Array.isArray(platforms?.platforms)) {
      dispatch(setFilterLoadedPlatform(platforms.platforms));
    }
  }, [platforms?.platforms, workspace]);

  // FILTER KEYWORDS
  useEffect(() => {
    // `keywordSource.search_terms` is scoped by `filterStore.countries`; a country with no
    // matching terms legitimately resolves to an empty array. Skipping the dispatch in that
    // case (as `.length > 0` used to) left the PREVIOUS country's keyword list in the filter
    // bar, making it look unfiltered by country. Only skip while the query hasn't resolved yet.
    if (!Array.isArray(keywordSource?.search_terms)) return;
    const hasTags = filterStore.tags.length > 0
    const hasSearchTerms = searchTermsByTags && searchTermsByTags?.search_terms && searchTermsByTags?.search_terms?.length > 0
    if (displayTags && hasTags && hasSearchTerms) {
      dispatch(setFilterLoadedKeyword(
        (keywordSource.search_terms as any[]).filter((term: any) => searchTermsByTags?.search_terms.map((st) => st.name).includes(term.search_term))))
    } else {
      dispatch(setFilterLoadedKeyword(keywordSource?.search_terms))
    }
  }, [keywordSource?.search_terms, workspace, searchTermsByTags, displayTags]);

  // FILTER DEVICES
  useEffect(() => {
    dispatch(setFilterLoadedDevice(['Mobile', 'Desktop', 'Tablet']))
  }, [])

  // FILTER TAGS
  useEffect(() => {
    if (tags) {
      dispatch(setFilterLoadedTags(tags?.map((tag: any) => ({ id: tag.id, label: tag.name }))))
    }
  }, [tags, workspace]);

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {(lgUp) &&
        <>

          {!window.location.pathname.startsWith("/workspaces/") && (filterStore?.minDatePicker) &&
            location?.pathname !== "/brand-overview" &&
            location?.pathname !== "/brand/search-performance/overview" &&
            location?.pathname !== "/brand/visibility/competitors" &&
            location?.pathname !== "/" &&
            location?.pathname !== "/admin/users" &&
            !location?.pathname.startsWith("/brand/campaign-leaks") &&
            location?.pathname !== "/account" &&
            location?.pathname !== "/workspaces" &&
            location?.pathname !== "/brand/search-performance/conversions" &&
            location?.pathname !== "/admin/cockpit" &&
            location?.pathname !== "/admin/sso" &&
            location?.pathname !== "/admin/sso/list" &&
            location?.pathname !== "/competitors/leaderboard/competitors-selection" &&
            location?.pathname !== "/competitors/leaderboard/tracked-competitors" &&
            location?.pathname !== "/competitors/leaderboard/market-leaders" &&
            location?.pathname !== "/brand/brand-shield/keywords/exact" &&
            location?.pathname !== "/404" &&
            !location?.pathname.startsWith("/brand/brand-shield") &&
            location?.pathname !== "/competitors/conquest-mode" &&
            location?.pathname !== "/brand/search-performance/cluster-manager" &&
            location?.pathname !== "/non-brand/search-performance/cluster-manager" &&
            location?.pathname !== "/brand/search-performance/traffic" &&
            location?.pathname !== "/non-brand/search-performance" &&
            location?.pathname !== "/brand/search-performance/explore" &&
            <Stack width={"100%"}>
              <Box>
                <Stack sx={{ height: 54, display: 'flex' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!["/non-brand/search-performance", "/non-brand/visibility", "/brand/visibility/overview", "/brand/visibility/ads", "/brand/visibility/search-terms", "/brand/visibility/local", "/visibility/competitive-landscape", "/brand/visibility/competitors", "/competitive-intelligence/overview", "/competitive-intelligence/ads", "/competitive-intelligence/st-analysis", "/competitive-intelligence/competitive-landscape"].includes(location.pathname) && (
                      <>
                        {(filterStore?.loadedCountries?.length >= 2) &&
                          <Autocomplete
                            multiple={true}
                            disableClearable={false}
                            size="small"
                            limitTags={1}
                            disableCloseOnSelect
                            options={filterStore?.loadedCountries}
                            getOptionLabel={(option: any) => option}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{ minWidth: 240, maxWidth: 300 }}
                              />
                            )}
                            value={
                              filterStore?.countries?.length === 0
                                ? [t('dashboard.filters.all_countries')]
                                : filterStore?.countries
                            }
                            renderOption={(props, option) => {
                              const country = countriesJson?.find((c) => c.name === option);
                              const flagIcon = country ? `circle-flags:${country.code}` : null;

                              return (
                                <li {...props} ref={(el) => (optionRefs.current[option] = el)}
                                  style={{ whiteSpace: 'nowrap' }} key={option} onMouseEnter={() => {
                                    optionRefs.current[option].classList.add('hovered');
                                  }}
                                  onMouseLeave={() => {
                                    optionRefs.current[option].classList.remove('hovered');
                                  }}>
                                  {option !== t('dashboard.filters.all') && (
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      width="100%"
                                      sx={{
                                        '&:hover .only-button': {
                                          display: 'flex',
                                        },
                                      }}
                                    >
                                      <Box>
                                        {location.pathname !== "/brand-wayback" &&
                                          <Checkbox
                                            checked={filterStore?.countries.includes(option)}
                                          />
                                        }

                                        {flagIcon ? (
                                          <Iconify icon={flagIcon} width={16} sx={{ mr: 1, position: 'relative', top: 4 }} />
                                        ) : (
                                          <img style={{ width: 14, height: 14, marginRight: 6, position: 'relative', top: -6 }} src="/assets/location.png" alt="flag" />
                                        )}
                                        <span style={{ position: 'relative', top: 2 }}>{option}</span>
                                      </Box>
                                      {location.pathname !== "/brand-wayback" &&
                                        <Button
                                          className="hover-only-button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOnlyFilter(option);
                                          }}
                                          variant="contained"
                                          color="inherit"
                                          size="small"
                                          sx={{
                                            mt: 0.3,
                                            height: 24,
                                            minWidth: 50,
                                            padding: 0,
                                            display: 'none',
                                            justifyContent: 'flex-end',
                                          }}
                                        >
                                          Only
                                        </Button>
                                      }
                                    </Box>
                                  )}
                                  {option === t('dashboard.filters.all_countries') && option}
                                </li>
                              );
                            }}
                            onChange={(event, selectedOptions) => handleCountryFilterChange(selectedOptions)}
                            renderTags={(selected, getTagProps) => {
                              const visibleTags = selected.slice(0, 1); // n’en afficher qu’un
                              const hiddenCount = selected.length - visibleTags.length;

                              return (
                                <>
                                  {visibleTags.map((option, index) => {
                                    const country = countriesJson?.find((c) => c.name === option);
                                    const flagCode = country ? country.code : null;

                                    return (
                                      <Chip
                                        {...getTagProps({ index })}
                                        key={option}
                                        icon={
                                          option !== t('dashboard.filters.all_countries') ? (
                                            flagCode ? (
                                              <FlagIcon
                                                key={flagCode}
                                                code={flagCode}
                                                sx={{ width: 16, height: 16, borderRadius: '50%' }}
                                              />
                                            ) : (
                                              <img style={{ width: 14, height: 14, marginRight: 1 }} src="/assets/location.png" alt="flag" />
                                            )
                                          ) : undefined
                                        }
                                        label={option}
                                        sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                                        size="small"
                                        deleteIcon={option === t('dashboard.filters.all_countries') ? <></> : undefined}
                                        variant="soft"
                                      />
                                    );
                                  })}

                                  {hiddenCount > 0 && (
                                    <Chip
                                      label={`+${hiddenCount}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </>
                              );
                            }}

                          />
                        }
                      </>
                    )}

                    {filterStore?.loadedPlatform?.length >= 2 &&
                      <Autocomplete
                        multiple={true}
                        limitTags={1}
                        disableClearable={false}
                        size="small"
                        options={filterStore?.loadedPlatform}
                        getOptionLabel={(option: any) => option}
                        renderInput={(params) => (<TextField {...params} />)}
                        value={filterStore?.platforms?.length === 0 ? [t('dashboard.filters.all_platforms')] : filterStore?.platforms}
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            {option === 'Google' || option === 'Bing' ? (
                              <Iconify
                                icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                sx={{ width: 16, height: 16, mr: 1 }}
                              />
                            ) : null}
                            {option}
                          </li>
                        )}
                        onChange={(event, selectedOptions) => {
                          let updatedOptions;

                          if (Array.isArray(selectedOptions)) {
                            updatedOptions = selectedOptions.filter(
                              (option: string) => option !== t('dashboard.filters.all_platforms')
                            );
                          } else {
                            updatedOptions = selectedOptions !== t('dashboard.filters.all_platforms') ? [selectedOptions] : [];
                          }

                          dispatch(setFilterPlatform(updatedOptions));
                        }}
                        renderTags={(selected, getTagProps) =>
                          selected.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              icon={
                                option === 'Google' || option === 'Bing' ? (
                                  <Iconify
                                    icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                    sx={{ width: 16, height: 16 }}
                                  />
                                ) : undefined
                              }
                              size="small"
                              sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                              deleteIcon={option === t('dashboard.filters.all_platforms') ? <></> : undefined}
                              variant="soft"
                            />
                          ))
                        }
                      />
                    }

                    {!["/non-brand/search-performance", "/text-ads", "/product-listing-ads", "/non-brand/visibility", "/brand/visibility/overview", "/brand/visibility/ads", "/brand/visibility/search-terms", "/brand/visibility/local", "/visibility/competitive-landscape", "/brand/visibility/competitors"].includes(location.pathname) && (

                      <Autocomplete
                        sx={{
                          width: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          "& .MuiAutocomplete-inputRoot": {
                            flexWrap: "nowrap",
                          },
                        }}
                        multiple={true}
                        size="small"
                        options={filterStore?.loadedDevices}
                        getOptionLabel={(option: any) => option}
                        renderInput={(params) => (
                          <TextField {...params} />
                        )}
                        value={
                          filterStore?.devices?.length === 0
                            ? [t('dashboard.filters.all_devices')]
                            : filterStore?.devices
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            <Iconify
                              icon={
                                iconDevices.find(
                                  (device: any) =>
                                    device.name.toLowerCase() === option.toLowerCase()
                                )?.icon
                              }
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                          </li>
                        )}
                        onChange={(event, selectedOptions) => handleDeviceFilterChange(selectedOptions)}
                        renderTags={(value, getTagProps) => {
                          const MAX_TAGS = 1;
                          const ALL = t('dashboard.filters.all_devices');

                          // 1) On retire le placeholder du calcul
                          const clean = (value || []).filter((v) => v !== ALL);

                          // 2) Si rien de sélectionné -> on affiche juste le placeholder (pas de +N)
                          if (clean.length === 0) {
                            return (
                              <Chip
                                key="all-devices"
                                label={ALL}
                                size="small"
                                variant="soft"
                                sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}

                              />
                            );
                          }

                          // 3) Calcul "shown / hidden" uniquement sur les vraies valeurs
                          const shown = clean.slice(0, MAX_TAGS);
                          const hidden = clean.length - shown.length;

                          return [
                            ...shown.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                                icon={
                                  <Iconify
                                    icon={iconDevices.find((d: any) => d.name.toLowerCase() === option.toLowerCase())?.icon}
                                    sx={{ width: 16, height: 16 }}
                                  />
                                }
                                sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                                size="small"
                                variant="soft"
                              />
                            )),
                            hidden >= 1 ? ( // <--- pas de +N quand il n'y a qu’un seul élément
                              <Chip key="more" label={`+${hidden}`} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }} />
                            ) : null,
                          ];
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            </Stack>
          }
        </>
      }

      <Stack
        flexGrow={1}
        display={"flex"}
        height={52}
        direction="row"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Box sx={{ height: "100%" }} display={"flex"} flexDirection={"row"}>
          {filterStore.minDatePicker &&
            <>
              {/* Visibility > Local porte sa propre période et sa comparaison dans
                  la toolbar de sa grid : deux contrôles de dates à l'écran
                  sinon. */}
              {!["/brand/visibility/local", "/competitors/leaderboard/market-leaders", "/brand/brand-shield/campaigns", "/brand/brand-shield/bid-strat-testing", "/brand/brand-shield/brand-infringement", "/brand/brand-shield/keywords/other", "/brand/brand-shield/keywords/exact", "/competitors/conquest-mode", "/competitors/leaderboard/tracked-competitors", "/competitors/leaderboard/competitors-selection", "/404", "/admin/sso/list", "/", "/admin/features/live-monitoring/add", "/admin/features/live-monitoring", "/workspaces", "/workspaces/add", "/admin/users", "/admin/users/add"].includes(location.pathname) &&
                // Campaign Leaks: each tab has its own period (fixed window, or toolbar selectors).
                !location.pathname.startsWith("/brand/campaign-leaks") &&
                <>
                  {/* Non Brand Search Performance is monthly: it gets the month picker instead. */}
                  {lgUp && location.pathname === NON_BRAND_SEARCH_PERF_PATH && (
                    <MonthRangeSelect
                      value={toMonthRangeValue(filterStore)}
                      onApply={(applied) => {
                        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        monthRangeActions(applied, timeZone).forEach((action) => dispatch(action));
                      }}
                    />
                  )}
                  {lgUp && location.pathname !== NON_BRAND_SEARCH_PERF_PATH && (
                    <DateRangeSelect
                      startDate={
                        routesWithOwnDateDefaults.includes(location.pathname) && filterStore?.start
                          ? new Date(filterStore.start)
                          : (dateRangeSelect.start || new Date())
                      }
                      endDate={
                        routesWithOwnDateDefaults.includes(location.pathname) && filterStore?.end
                          ? new Date(filterStore.end)
                          : (dateRangeSelect.end || new Date())
                      }
                      compareStartDate={undefined}
                      compareEndDate={undefined}
                      selected={
                        dateRangeSelect.selected === "custom"
                          ? `${fDate(filterStore.start)} - ${fDate(filterStore.end)}`
                          : (dateRangeSelect.preset as any).displayName
                      }
                      onChangeEndDate={(endDate: Date | null) => {
                        dateRangeSelect.onChangeEndDate(endDate);
                        updateFilterDate(filterStore?.start, endDate);
                      }}
                      onChangeStartDate={(startDate: Date | null) => {
                        dateRangeSelect.onChangeStartDate(startDate);
                        updateFilterDate(startDate, filterStore?.end);
                      }}
                      // Atomic range apply: set both bounds in one dispatch so neither reverts
                      // to a stale store value. Required for routes whose date default is owned
                      // by the page (routesWithOwnDateDefaults), where the [dateRangeSelect]
                      // corrective effect is disabled and can't re-sync the picker selection.
                      onChangeRange={(startDate: Date | null, endDate: Date | null) => {
                        dateRangeSelect.onChangeStartDate(startDate);
                        dateRangeSelect.onChangeEndDate(endDate);
                        updateFilterDate(startDate, endDate);
                      }}
                      onChangeCompareStartDate={(it: any) => setCompareStartDate(it)}
                      onChangeCompareEndDate={(it: any) => setCompareEndDate(it)}
                      onChangePreset={onChangePreset}
                      onChangeTimeStep={onChangeTimeStep}
                      preset={"last_30d"}
                      features={{ time_step: true }}
                      timeStep={dateRangeSelect.step as any}
                    />
                  )}
                </>
              }
            </>
          }

          <Box sx={{ ml: 1 }}>
            <NotificationsPopover />
          </Box>

          <SettingsPanel open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)} />

          <AccountPopover />
        </Box>
      </Stack>
    </>

  );

  const headerHeight =
    lgUp
      ? (isNavHorizontal
        ? HEADER.H_DESKTOP_OFFSET
        : (offsetTop ? HEADER.H_DESKTOP_OFFSET : HEADER.H_DESKTOP))
      : HEADER.H_MOBILE;

  const matchLocation = (key: string) => {
    if (key.startsWith("regexp:")) {
      return new RegExp(key.replace("regexp:", "")).test(location.pathname);
    }
    return location.pathname.startsWith(key);
  }

  const getLocationParams = (key: string) => {
    if (key.startsWith("regexp:")) {
      const regex = new RegExp(key.replace("regexp:", ""));
      const match = regex.exec(location.pathname);
      return match?.groups || {};
    }
  }


  // Trouver le bon set de tabs
  const [currentPath, currentTabs] = Object.entries(tabs)
    .sort(([a,], [b,]) => b.length - a.length)
    .find(([key, value]) => matchLocation(key)) || ["", []] as any;


  const currentParams = getLocationParams(currentPath);

  const currentTitle = currentTabs.find((tab: any) => tab.path === location.pathname)?.title || currentTabs[0]?.title || "";
  const redesignedHeader = usesRedesignedHeader(location.pathname);

  // Platforms pill: after the search terms on Non Brand Search Performance (maquette order), before elsewhere.
  const platformsPill = (
    (location?.pathname !== "/brand/visibility/competitors" && location?.pathname !== "/brand/visibility/search-terms") &&
      <>
        {displayTags && showPlatformsPill(pillPlatforms.length) && (
          <Box mr={1}>
            <Autocomplete
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  flexWrap: "nowrap",
                  height: "36px",
                  paddingTop: 0,
                  paddingBottom: 0,
                },

                "& .MuiChip-root": {
                  backgroundColor: "#F4F6F8",
                  color: "#212B36",

                  "&:hover": {
                    backgroundColor: "#DFE3E8",
                  },
                },
              }}
              multiple={true}
              limitTags={1}
              disableClearable={false}
              size="small"
              options={pillPlatforms}
              getOptionLabel={(option: any) => option}
              renderInput={(params) => (<TextField {...params} />)}
              value={filterStore?.platforms?.length === 0 ? [t('dashboard.filters.all_platforms')] : filterStore?.platforms}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option === 'Google' || option === 'Bing' ? (
                    <Iconify
                      icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                      sx={{ width: 16, height: 16, mr: 1 }}
                    />
                  ) : null}
                  {option}
                </li>
              )}
              onChange={(event, selectedOptions) => {
                let updatedOptions;
                if (Array.isArray(selectedOptions)) {
                  updatedOptions = selectedOptions.filter(
                    (option: string) => option !== t('dashboard.filters.all_platforms')
                  );
                } else {
                  updatedOptions = selectedOptions !== t('dashboard.filters.all_platforms') ? [selectedOptions] : [];
                }
                dispatch(setFilterPlatform(updatedOptions));
              }}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    icon={
                      option === 'Google' || option === 'Bing' ? (
                        <Iconify
                          icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                          sx={{ width: 16, height: 16 }}
                        />
                      ) : undefined
                    }
                    size="small"
                    sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)' }}
                    deleteIcon={option === t('dashboard.filters.all_platforms') ? <></> : undefined}
                    variant="soft"
                  />
                ))
              }
            />
          </Box>
        )}
      </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,

        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], { duration: theme.transitions.duration.shorter }),
        left: navPinned ? NAV.W_VERTICAL_EXPANDED : NAV.W_VERTICAL,
        width: navPinned
          ? `calc(100% - ${NAV.W_VERTICAL_EXPANDED}px)`
          : `calc(100% - ${NAV.W_VERTICAL}px)`,
        ...(isNavHorizontal && {
          left: 0,
          width: 1,
          bgcolor: 'background.default',
          height: HEADER.H_DESKTOP_OFFSET,
        }),
        backgroundColor: "#17191a"
      }}
    >

      <Toolbar
        sx={{
          height: 1,
        }}
      >
        {!["/brand/brand-shield/active-mode", "/brand/brand-shield/ko-bidding", "/brand/brand-shield/ko-bidding-auto"].includes(location.pathname) &&
          <>
            {renderContent}
          </>
        }
      </Toolbar>


      {currentPath &&
        <Stack
          className="header-tabs-container"
          width="100%"
          sx={{
            position: 'fixed',
            top: headerHeight,
            left: 0,
            width: '100%',
            zIndex: theme.zIndex.appBar,
            background: redesignedHeader ? "#FCFCFC" : "white",
            borderTopLeftRadius: lgUp ? 16 : 0,
            borderBottom: (t) => `1.5px solid ${t.palette.divider}`,
          }}
        >
          <Box
            sx={{
              paddingX: "24px",
              gap: "20px",
            }}
            height={54}
            display="flex"
            flexDirection="row"
            alignItems="center"
            component="div"
          >
            {/* Titre courant */}
            <Typography
              fontSize={19}
              fontWeight={700}
              sx={redesignedHeader ? { fontSize: 18, lineHeight: "18px", color: "#161C24" } : undefined}
            >
              {currentTitle}
            </Typography>

            {/* Tabs dynamiques */}
            <HeaderTabs currentPath={currentPath} role={role} redesigned={redesignedHeader} />
          </Box>
          {!(
            ROUTES_WITHOUT_FILTER_BAR.includes(location.pathname)
            || window.location.pathname.startsWith("/workspaces/")
          ) && (
              <Stack
                sx={{
                  height: 56,
                  backgroundColor: theme.palette.mode === "dark" ? "#2f363f" : 'white',
                  pl: 3,
                  pr: 5,
                  borderTop: "1px solid rgba(145, 158, 171, 0.2)",
                  display: "flex",
                }}
              >
                <Box
                  height={"100%"}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  sx={{ "& .MuiChip-root": { borderRadius: "6px" } }}
                >

                  {(location?.pathname !== "/brand/visibility/competitors" && location?.pathname !== "/brand/visibility/search-terms") &&
                    <>
                      {displayTags && (
                        <Box mr={1}>
                          <Autocomplete
                            sx={{
                              width: 180,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",

                              "& .MuiAutocomplete-inputRoot": {
                                flexWrap: "nowrap",
                                height: '36px',
                                paddingTop: 0,
                                paddingBottom: 0,
                              },

                              "& .MuiChip-root": {
                                backgroundColor: "#F4F6F8",

                                "&:hover": {
                                  backgroundColor: "#DFE3E8",
                                },
                              },
                            }}
                            multiple={true}
                            size="small"
                            options={filterStore?.loadedDevices}
                            getOptionLabel={(option: any) => option}
                            renderInput={(params) => (
                              <TextField {...params} />
                            )}
                            value={
                              filterStore?.devices?.length === 0
                                ? [t('dashboard.filters.all_devices')]
                                : filterStore?.devices
                            }
                            renderOption={(props, option) => (
                              <li {...props} key={option}>
                                <Iconify
                                  icon={
                                    iconDevices.find(
                                      (device: any) =>
                                        device.name.toLowerCase() === option.toLowerCase()
                                    )?.icon
                                  }
                                  sx={{ width: 16, height: 16, mr: 1 }}
                                />
                                {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                              </li>
                            )}
                            onChange={(event, selectedOptions) => handleDeviceFilterChange(selectedOptions)}
                            renderTags={(value, getTagProps) => {
                              const MAX_TAGS = 1;
                              const ALL = t('dashboard.filters.all_devices');

                              const clean = (value || []).filter((v) => v !== ALL);

                              if (clean.length === 0) {
                                return (
                                  <Chip
                                    key="all-devices"
                                    label={ALL}
                                    size="small"
                                    variant="soft"
                                    sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)' }}
                                  />
                                );
                              }

                              const shown = clean.slice(0, MAX_TAGS);
                              const hidden = clean.length - shown.length;

                              return [
                                ...shown.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                                    icon={
                                      <Iconify
                                        icon={iconDevices.find((d: any) => d.name.toLowerCase() === option.toLowerCase())?.icon}
                                        sx={{ width: 16, height: 16 }}
                                      />
                                    }
                                    size="small"
                                    variant="soft"
                                    sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)' }}
                                  />
                                )),
                                hidden >= 1 ? (
                                  <Chip key="more" label={`+${hidden}`} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)' }} />
                                ) : null,
                              ];
                            }}
                          />
                        </Box>
                      )}
                    </>
                  }

                  {!onNonBrandSearchPerf && platformsPill}

                  {/* Hidden on the Non Brand Cluster Manager: its options are Brand search terms. */}
                  {!["/brand/search-performance/overview", "/brand/visibility/ads", "/brand/visibility/search-terms", "/brand/visibility/local", "/brand/visibility/competitors", "/brand-overview", "/brand/brand-shield/keywords/exact", "/brand/brand-shield/keywords/other", "/brand-wayback", "/conversions-analysis", "/non-brand/search-performance/cluster-manager"].includes(location.pathname) && (
                    <Box mr={1}>
                      <InfiniteScrollKeywords
                        keywords={(filterStore?.loadedKeyword || [])}
                        onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                      />
                    </Box>
                  )}

                  {onNonBrandSearchPerf && platformsPill}

                  {location.pathname === "/brand/visibility/ads" && (
                    <VisibilityAdsHeader />
                  )}

                  {displayTags
                    && showClustersPill(location.pathname, filterStore.loadedTags.length) && (
                      <Box>
                        <GenericInfiniteScrollList
                          limitTags={2}
                          sx={{ width: 275 }}
                          fullWidth={true}
                          searchPlaceholder=""
                          items={(filterStore as RootState["filters"]).loadedTags}
                          options={(filterStore as RootState["filters"]).loadedTags}
                          multiple={true}
                          value={
                            filterStore.tags.length === 0
                              ? [{ id: "__default__", label: "All Clusters" }]
                              : filterStore.tags
                          }
                          onSelectionChange={(selected) => {
                            dispatch(setFilterTags(pickedClusters(selected, filterStore.loadedTags)));
                          }}
                          getOptionLabel={(item: any) => item?.label || (item as any)}
                          getOptionKey={(item: any) => item.id || (item as any)}
                          isOptionEqualToValue={(item, value) => (item.id || item) === (value.id || value)}
                          customItemRenderer={(item) => (
                            <Chip
                              label={item.label || (item as any)}
                              size="small"
                              variant="soft"
                              sx={{
                                backgroundColor: item?.label
                                  ? generateColorFromString(String(item.label))
                                  : undefined,
                                color: "#fff"
                              }}
                            />
                          )}
                          customTagRenderer={(item) => {
                            const isDefault = item.id === "__default__";

                            return (
                              <Chip
                                label={item.label}
                                size="small"
                                variant={isDefault ? "soft" : "soft"} // style différent
                                sx={{
                                  fontSize: '13px',
                                  backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                  color: 'rgba(51, 60, 71, 1)',
                                }}
                                onDelete={
                                  isDefault
                                    ? undefined
                                    : () => {
                                      dispatch(
                                        setFilterTags(
                                          filterStore.tags.filter(
                                            (tag: any) => tag.id !== item.id
                                          ) as any
                                        )
                                      );
                                    }
                                }
                              />
                            );
                          }}
                        />
                      </Box>
                    )}

                  {onNonBrandSearchPerf && (
                    <Box ml={1}>
                      <OpportunityFilterPill />
                    </Box>
                  )}

                  {["/brand/search-performance/overview"].includes(location.pathname) && (

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Autocomplete
                        options={savingsFormulaOptions}
                        value={savingsFormulaFilter}
                        disableClearable
                        onChange={(_, newValue) => {
                          dispatch(setSavingsFormulaFilter(newValue?.value || "m1"));
                        }}
                        sx={{ width: 200 }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
        </Stack>
      }
    </AppBar>
  );
}
